import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useDataCache } from "../context/DataCacheContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  FileSpreadsheet, Loader2, Search, Save, ArrowLeft,
  FileText, Receipt, Banknote, CheckCircle,
  Calculator, TrendingUp, TrendingDown, Download, ChevronDown, Eye, RefreshCw,
  Scale, Gavel, Merge, AlertTriangle, Trash2, ArrowUpDown
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../config/api";
import { format } from "date-fns";
import { normalizeForSearch, extractNumarIntern, getPaymentStatus } from "../utils/helpers";
import { EtapaFinanciaraForm } from "../components/evidence/EtapaFinanciaraForm";


// Etape pentru dosare CIVILE
const ETAPE_CIVIL = [
  { key: "faza_prejudiciara", label: "Fază Prejudiciară", color: "bg-purple-500" },
  { key: "fond", label: "Fond", color: "bg-blue-500" },
  { key: "apel", label: "Apel", color: "bg-orange-500" },
  { key: "recurs", label: "Recurs", color: "bg-teal-600" },
  { key: "executare_silita", label: "Executare Silită", color: "bg-red-500" },
];

// Etape pentru dosare PENALE (cu Cameră Preliminară și Executare Silită pentru despăgubiri)
const ETAPE_PENAL = [
  { key: "urmarire_penala", label: "Urmărire Penală", color: "bg-purple-500" },
  { key: "camera_preliminara", label: "Cameră Preliminară", color: "bg-indigo-500" },
  { key: "fond", label: "Fond", color: "bg-blue-500" },
  { key: "apel", label: "Apel", color: "bg-orange-500" },
  { key: "recurs", label: "Recurs", color: "bg-teal-600" },
  { key: "executare_silita_penal", label: "Executare Silită (Despăgubiri)", color: "bg-red-500" },
];


export default function EvidentaDosarePage() {
  const { user, getAuthHeader } = useAuth();
  const { invalidateCache } = useDataCache();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Sort & Filter state
  const [sortBy, setSortBy] = useState("intern"); // "intern" or "restant"
  const [statusFilter, setStatusFilter] = useState("toate"); // "toate", "incasat", "partial", "neincasat"
  
  // Merge state
  const [mergeMode, setMergeMode] = useState(false);
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  
  const isAdmin = user?.role === "admin";

  const fetchRecords = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/evidenta-dosare`, getAuthHeader());
      setRecords(response.data);
      
      // Also update editData if a record is currently selected
      if (selectedRecord?.id) {
        const updatedRecord = response.data.find(r => r.id === selectedRecord.id);
        if (updatedRecord) {
          setEditData(updatedRecord);
        }
      }
    } catch (error) {
      console.error("Failed to fetch evidenta dosare:", error);
      toast.error("Eroare la încărcarea evidențelor");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, selectedRecord?.id]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Handle URL param for opening specific record
  useEffect(() => {
    const caseId = searchParams.get("case");
    if (caseId && records.length > 0) {
      const record = records.find(r => r.case_id === caseId);
      if (record) {
        setSelectedRecord(record);
        setEditData(record);
        setSearchParams({});
      }
    }
  }, [searchParams, records, setSearchParams]);

  // Global totals across all (filtered) records
  const globalTotals = useMemo(() => {
    let totalFacturat = 0, totalIncasat = 0, totalRestant = 0, partialCount = 0;
    records.forEach(r => {
      totalFacturat += r.total_facturi || 0;
      totalIncasat += r.total_incasat || 0;
      totalRestant += r.total_neincasat || 0;
      const status = getPaymentStatus(r);
      if (status === "partial") partialCount++;
    });
    return { totalFacturat, totalIncasat, totalRestant, partialCount };
  }, [records]);

  // Filter and sort records
  const filteredRecords = useMemo(() => {
    let result = [...records];
    
    if (searchQuery.trim()) {
      const query = normalizeForSearch(searchQuery);
      result = result.filter(r => 
        normalizeForSearch(r.numar_dosar).includes(query) ||
        normalizeForSearch(r.numar_dosar_intern).includes(query) ||
        normalizeForSearch(r.nume_client).includes(query)
      );
    }
    
    // Filter by payment status
    if (statusFilter !== "toate") {
      result = result.filter(r => {
        const status = getPaymentStatus(r);
        if (statusFilter === "incasat") return status === "incasat";
        if (statusFilter === "partial") return status === "partial";
        if (statusFilter === "neincasat") return status === "neincasat" || status === "fara_date";
        return true;
      });
    }
    
    // Sort
    if (sortBy === "restant") {
      result.sort((a, b) => (b.total_neincasat || 0) - (a.total_neincasat || 0));
    } else {
      result.sort((a, b) => {
        const numA = extractNumarIntern(a.numar_dosar_intern);
        const numB = extractNumarIntern(b.numar_dosar_intern);
        if (numA !== null && numB !== null) return numA - numB;
        if (numA !== null) return -1;
        if (numB !== null) return 1;
        return (a.nume_client || '').localeCompare(b.nume_client || '', 'ro');
      });
    }
    
    return result;
  }, [records, searchQuery, sortBy, statusFilter]);

  const handleSelectRecord = (record) => {
    setSelectedRecord(record);
    setEditData(record);
  };

  const handleBack = () => {
    setSelectedRecord(null);
    setEditData(null);
  };

  const handleSave = async () => {
    if (!editData) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/evidenta-dosare/${editData.id}`, {
        observatii: editData.observatii,
        tip_dosar: editData.tip_dosar,
        faza_prejudiciara: editData.faza_prejudiciara,
        fond: editData.fond,
        apel: editData.apel,
        recurs: editData.recurs,
        executare_silita: editData.executare_silita,
        urmarire_penala: editData.urmarire_penala,
        camera_preliminara: editData.camera_preliminara,
      }, getAuthHeader());
      toast.success("Evidența a fost actualizată");
      await fetchRecords();
      // Update selected record with new data
      const updated = await axios.get(`${API}/evidenta-dosare/${editData.id}`, getAuthHeader());
      setSelectedRecord(updated.data);
      setEditData(updated.data);
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const handleEtapaChange = (etapaKey, newData) => {
    setEditData(prev => ({
      ...prev,
      [etapaKey]: newData
    }));
  };

  // Calculate totals from editData for the colored summary cards
  const calculatedTotals = useMemo(() => {
    if (!editData) return { totalFacturi: 0, totalPlati: 0, totalIncasat: 0, totalNeincasat: 0 };
    
    const etapeKeys = editData.tip_dosar === "penal" 
      ? ["urmarire_penala", "camera_preliminara", "fond", "apel", "recurs", "executare_silita_penal"]
      : ["faza_prejudiciara", "fond", "apel", "recurs", "executare_silita"];
    
    let totalFacturi = 0;
    let totalPlati = 0;
    let totalIncasat = 0;
    
    etapeKeys.forEach(key => {
      const etapa = editData[key] || {};
      // Legacy single factura
      totalFacturi += parseFloat(etapa.factura_valoare) || 0;
      // Multiple facturi
      (etapa.facturi || []).forEach(f => {
        totalFacturi += parseFloat(f.valoare) || 0;
      });
      // Legacy single chitanta (plată)
      totalPlati += parseFloat(etapa.chitanta_valoare) || 0;
      // Multiple chitante (plăți)
      (etapa.chitante || []).forEach(c => {
        totalPlati += parseFloat(c.valoare) || 0;
      });
      // Suma incasata efectiv - aceasta este "Total Încasat"
      totalIncasat += parseFloat(etapa.suma_incasata_efectiv) || 0;
    });
    
    // Total neîncasat = Total Facturat - Total Încasat (suma efectiv încasată)
    const totalNeincasat = totalFacturi - totalIncasat;
    
    return { totalFacturi, totalPlati, totalIncasat, totalNeincasat: totalNeincasat > 0 ? totalNeincasat : 0 };
  }, [editData]);

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/evidenta-dosare/sync-all`, {}, getAuthHeader());
      const msg = `Sincronizare completă: ${response.data.created} create, ${response.data.linked || 0} legate, ${response.data.skipped} existente`;
      toast.success(msg);
      await fetchRecords();
    } catch (error) {
      console.error("Sync error:", error);
      toast.error("Eroare la sincronizare");
    } finally {
      setSyncing(false);
    }
  };

  // Merge functions
  const [targetEvidenceId, setTargetEvidenceId] = useState(null);
  
  const handleToggleMergeMode = () => {
    setMergeMode(!mergeMode);
    setSelectedForMerge([]);
    setTargetEvidenceId(null);
  };

  const handleToggleSelectForMerge = (recordId) => {
    setSelectedForMerge(prev => {
      if (prev.includes(recordId)) {
        return prev.filter(id => id !== recordId);
      }
      if (prev.length < 2) {
        return [...prev, recordId];
      }
      return prev;
    });
  };

  const handleOpenMergeDialog = () => {
    // Default: second selected is target (will be kept)
    if (selectedForMerge.length === 2) {
      setTargetEvidenceId(selectedForMerge[1]);
    }
    setMergeDialogOpen(true);
  };

  const handleMerge = async () => {
    if (selectedForMerge.length !== 2 || !targetEvidenceId) return;
    
    setMerging(true);
    try {
      // Source is the one NOT selected as target
      const sourceId = selectedForMerge.find(id => id !== targetEvidenceId);
      await axios.post(`${API}/evidenta-dosare/merge`, {
        source_id: sourceId,
        target_id: targetEvidenceId
      }, getAuthHeader());
      
      toast.success("Evidențele au fost combinate cu succes");
      setMergeDialogOpen(false);
      setSelectedForMerge([]);
      setTargetEvidenceId(null);
      setMergeMode(false);
      await fetchRecords();
    } catch (error) {
      console.error("Merge error:", error);
      toast.error(error.response?.data?.detail || "Eroare la combinare");
    } finally {
      setMerging(false);
    }
  };

  const getSelectedMergeRecords = () => {
    return selectedForMerge.map(id => records.find(r => r.id === id)).filter(Boolean);
  };

  const handleDeleteEvidenta = async (evidenta_id, numar_dosar) => {
    if (!window.confirm(`Sigur doriți să ștergeți evidența pentru dosarul ${numar_dosar || 'N/A'}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/evidenta-dosare/${evidenta_id}`, getAuthHeader());
      toast.success("Evidența a fost ștearsă");
      // Remove from local state immediately
      setRecords(prev => prev.filter(r => r.id !== evidenta_id));
      // Also invalidate cache to ensure fresh data on next load
      invalidateCache('evidenceDosare');
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || "Eroare la ștergere");
    }
  };

  const handleExportJson = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/evidenta-dosare/export/json`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `evidenta_dosare_${format(new Date(), 'yyyyMMdd_HHmm')}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export JSON realizat");
    } catch (error) {
      toast.error("Eroare la export");
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/evidenta-dosare/export/excel`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `evidenta_dosare_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export Excel realizat");
    } catch (error) {
      toast.error("Eroare la export");
    } finally {
      setExporting(false);
    }
  };

  // Get etape based on tip_dosar
  const getEtapeConfig = (tipDosar) => {
    return tipDosar === "penal" ? ETAPE_PENAL : ETAPE_CIVIL;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Detail View
  if (selectedRecord) {
    const etapeConfig = getEtapeConfig(editData?.tip_dosar || "civil");
    
    return (
      <div className="space-y-6" data-testid="evidenta-detail-view">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="space-y-2">
            <Button variant="ghost" onClick={handleBack} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Înapoi la Evidențe dosare
            </Button>
            <div className="flex items-center gap-3 pl-2">
              <FileSpreadsheet className="w-6 h-6 text-primary" />
              <h1 className="font-heading text-2xl font-bold">
                Evidență Dosar {selectedRecord.numar_dosar || selectedRecord.numar_dosar_intern}
              </h1>
            </div>
          </div>
          {isAdmin && (
            <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Se salvează...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvează modificările
                </>
              )}
            </Button>
          )}
        </div>

        {/* Informații Generale */}
        <Card>
          <CardHeader>
            <CardTitle>Informații Generale</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Info preluată automat (needitabilă) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
              <div>
                <span className="text-sm text-muted-foreground">Nr. Dosar Intern</span>
                <p className="font-medium">{selectedRecord.numar_dosar_intern || "-"}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Nr. Dosar</span>
                <p className="font-medium">{selectedRecord.numar_dosar || "-"}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Nume Client</span>
                <p className="font-medium">{selectedRecord.nume_client || "-"}</p>
              </div>
            </div>
            
            {/* Observații - editabile */}
            <div className="space-y-2">
              <Label>Observații</Label>
              <Textarea
                value={editData?.observatii || ""}
                onChange={(e) => setEditData({...editData, observatii: e.target.value})}
                disabled={!isAdmin}
                rows={3}
                placeholder="Adăugați observații..."
              />
            </div>
            
            {/* Link către dosar */}
            {selectedRecord.case_id && (
              <Button
                variant="outline"
                onClick={() => navigate(`/cases?open=${selectedRecord.case_id}`)}
                data-testid="go-to-case-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Vezi Dosarul Principal
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Totals Summary - styled like EvidențaBC */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-blue-700">
                <Receipt className="w-4 h-4" />
                <span className="text-xs font-medium">Total Facturi</span>
              </div>
              <p className="text-2xl font-bold text-blue-900 mt-1">
                {calculatedTotals.totalFacturi.toFixed(2)} RON
              </p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-purple-700">
                <Banknote className="w-4 h-4" />
                <span className="text-xs font-medium">Total Plăți</span>
              </div>
              <p className="text-2xl font-bold text-purple-900 mt-1">
                {calculatedTotals.totalPlati.toFixed(2)} RON
              </p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-green-700">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs font-medium">Total Încasat</span>
              </div>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {calculatedTotals.totalIncasat.toFixed(2)} RON
              </p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-red-700">
                <TrendingDown className="w-4 h-4" />
                <span className="text-xs font-medium">Total Neîncasat</span>
              </div>
              <p className="text-2xl font-bold text-red-900 mt-1">
                {calculatedTotals.totalNeincasat.toFixed(2)} RON
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Etape Procesuale - with Tabs like EvidențaBC */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="text-lg">Etape Procesuale</CardTitle>
                <CardDescription>
                  Evidența financiară pentru fiecare etapă a dosarului
                </CardDescription>
              </div>
              {/* Selector tip dosar */}
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium">Tip Dosar:</Label>
                <Select
                  value={editData?.tip_dosar || "civil"}
                  onValueChange={(value) => setEditData({...editData, tip_dosar: value})}
                  disabled={!isAdmin}
                >
                  <SelectTrigger className="w-[160px]" data-testid="tip-dosar-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="civil">
                      <div className="flex items-center gap-2">
                        <Scale className="w-4 h-4" />
                        Civil
                      </div>
                    </SelectItem>
                    <SelectItem value="penal">
                      <div className="flex items-center gap-2">
                        <Gavel className="w-4 h-4" />
                        Penal
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={etapeConfig[0]?.key} className="w-full">
              <TabsList className={`grid w-full ${
                etapeConfig.length === 6 ? 'grid-cols-6' : 
                etapeConfig.length === 5 ? 'grid-cols-5' : 'grid-cols-4'
              }`}>
                {etapeConfig.map(etapa => (
                  <TabsTrigger key={etapa.key} value={etapa.key} className="text-xs sm:text-sm px-1 sm:px-2">
                    <span className="truncate">{etapa.label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {etapeConfig.map(etapa => (
                <TabsContent key={etapa.key} value={etapa.key}>
                  <EtapaFinanciaraForm
                    etapa={etapa}
                    data={editData?.[etapa.key] || {}}
                    onChange={(newData) => handleEtapaChange(etapa.key, newData)}
                    disabled={!isAdmin}
                  />
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6" data-testid="evidenta-dosare-page">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Evidență Dosare</h1>
          <p className="font-body text-muted-foreground mt-1">
            Evidența financiară pentru dosarele principale
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button
              variant={mergeMode ? "default" : "outline"}
              onClick={handleToggleMergeMode}
              data-testid="merge-mode-btn"
            >
              <Merge className="w-4 h-4 mr-2" />
              {mergeMode ? "Anulează Combinare" : "Combină Evidențe"}
            </Button>
            <Button
              variant="outline"
              onClick={handleSyncAll}
              disabled={syncing}
              data-testid="sync-all-btn"
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sincronizează Toate
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={exporting}>
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Exportă
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportJson}>
                  <FileText className="w-4 h-4 mr-2" />
                  Export JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportExcel}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export Excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => {
                  setExporting(true);
                  try {
                    const response = await axios.get(`${API}/export/evidenta-dosare-report`, {
                      ...getAuthHeader(),
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `evidenta_dosare_${format(new Date(), 'yyyyMMdd')}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("PDF exportat");
                  } catch (error) {
                    toast.error("Eroare la export PDF");
                  } finally {
                    setExporting(false);
                  }
                }} data-testid="export-pdf-evidenta">
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* 5. Global Summary Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="global-summary-bar">
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Total Facturat</p>
          <p className="text-xl font-bold text-blue-800 mt-1 tabular-nums">{globalTotals.totalFacturat.toLocaleString('ro-RO', {minimumFractionDigits: 2})} RON</p>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <p className="text-xs font-medium text-green-600 uppercase tracking-wide">Total Încasat</p>
          <p className="text-xl font-bold text-green-800 mt-1 tabular-nums">{globalTotals.totalIncasat.toLocaleString('ro-RO', {minimumFractionDigits: 2})} RON</p>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-xs font-medium text-red-600 uppercase tracking-wide">Total Restant</p>
          <p className="text-xl font-bold text-red-800 mt-1 tabular-nums">{globalTotals.totalRestant.toLocaleString('ro-RO', {minimumFractionDigits: 2})} RON</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">Parțial Încasate</p>
          <p className="text-xl font-bold text-amber-800 mt-1">{globalTotals.partialCount} dosare</p>
        </div>
      </div>

      {/* Merge Mode Banner */}
      {mergeMode && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">Mod Combinare Activ</span>
                <span className="text-sm">- Selectați 2 evidențe pentru a le combina ({selectedForMerge.length}/2 selectate)</span>
              </div>
              {selectedForMerge.length === 2 && (
                <Button onClick={handleOpenMergeDialog} data-testid="confirm-merge-btn">
                  <Merge className="w-4 h-4 mr-2" />
                  Combină Selectate
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3+4. Toolbar: Search + Sort + Filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Caută după număr dosar sau client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        
        {/* Sort buttons */}
        <Button
          variant={sortBy === "intern" ? "default" : "outline"}
          size="sm"
          onClick={() => setSortBy("intern")}
          data-testid="sort-intern-btn"
        >
          <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
          Nr. Intern
        </Button>
        <Button
          variant={sortBy === "restant" ? "default" : "outline"}
          size="sm"
          onClick={() => setSortBy("restant")}
          data-testid="sort-restant-btn"
        >
          <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
          Sold Restant
        </Button>
        
        <div className="h-6 w-px bg-border" />
        
        {/* Filter buttons */}
        {[
          { key: "toate", label: "Toate" },
          { key: "incasat", label: "Încasate" },
          { key: "partial", label: "Parțial" },
          { key: "neincasat", label: "Neîncasate" },
        ].map(f => (
          <Button
            key={f.key}
            variant={statusFilter === f.key ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(f.key)}
            className={statusFilter === f.key && f.key === "neincasat" ? "bg-red-600 hover:bg-red-700" : 
                        statusFilter === f.key && f.key === "partial" ? "bg-amber-600 hover:bg-amber-700" :
                        statusFilter === f.key && f.key === "incasat" ? "bg-green-600 hover:bg-green-700" : ""}
            data-testid={`filter-${f.key}-btn`}
          >
            {f.label}
          </Button>
        ))}
      </div>
      </div>{/* End Sticky Header */}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Toate Evidențele ({filteredRecords.length}{filteredRecords.length !== records.length ? ` din ${records.length}` : ''})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {records.length === 0 ? (
            <div className="text-center py-8">
              <FileSpreadsheet className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Nu există evidențe încă.</p>
              {isAdmin && (
                <Button variant="outline" className="mt-4" onClick={handleSyncAll}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sincronizează din Dosare
                </Button>
              )}
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {mergeMode && <TableHead className="w-10"></TableHead>}
                    <TableHead className="font-semibold">Nr. Intern</TableHead>
                    <TableHead className="font-semibold">Nr. Dosar</TableHead>
                    <TableHead className="font-semibold">Client</TableHead>
                    <TableHead className="font-semibold text-right">Facturat</TableHead>
                    <TableHead className="font-semibold text-right">Încasat</TableHead>
                    <TableHead className="font-semibold text-right">Restant</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecords.map((record) => {
                    const status = getPaymentStatus(record);
                    const borderColor = status === "incasat" ? "border-l-green-500" :
                                        status === "partial" ? "border-l-amber-500" :
                                        status === "neincasat" ? "border-l-red-500" : "border-l-gray-200";
                    return (
                      <TableRow 
                        key={record.id} 
                        className={`cursor-pointer hover:bg-muted/50 border-l-4 ${borderColor} ${
                          selectedForMerge.includes(record.id) ? 'bg-amber-50' : ''
                        }`}
                        onClick={() => mergeMode ? handleToggleSelectForMerge(record.id) : handleSelectRecord(record)}
                        data-testid={`evidenta-row-${record.id}`}
                      >
                        {mergeMode && (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedForMerge.includes(record.id)}
                              onCheckedChange={() => handleToggleSelectForMerge(record.id)}
                              disabled={selectedForMerge.length >= 2 && !selectedForMerge.includes(record.id)}
                            />
                          </TableCell>
                        )}
                        <TableCell className="font-mono text-sm">
                          {record.numar_dosar_intern || "-"}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {record.numar_dosar || "-"}
                        </TableCell>
                        <TableCell>{record.nume_client || "-"}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          {(record.total_facturi || 0).toLocaleString('ro-RO', {minimumFractionDigits: 2})}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-green-700">
                          {(record.total_incasat || 0).toLocaleString('ro-RO', {minimumFractionDigits: 2})}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-red-700">
                          {(record.total_neincasat || 0).toLocaleString('ro-RO', {minimumFractionDigits: 2})}
                        </TableCell>
                        <TableCell>
                          {status === "incasat" && <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Încasat</Badge>}
                          {status === "partial" && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Parțial</Badge>}
                          {status === "neincasat" && <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Neîncasat</Badge>}
                          {status === "fara_date" && <Badge variant="outline" className="text-gray-500">-</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectRecord(record);
                              }}
                              data-testid={`view-evidenta-${record.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteEvidenta(record.id, record.numar_dosar);
                                }}
                                data-testid={`delete-evidenta-${record.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      {records.length > 0 && (
        <div className="flex items-center gap-5 px-4 py-3 bg-muted/40 rounded-lg text-sm text-muted-foreground">
          <div className="flex items-center gap-2"><div className="w-3 h-1 rounded bg-green-500" /> Complet încasat</div>
          <div className="flex items-center gap-2"><div className="w-3 h-1 rounded bg-amber-500" /> Parțial încasat</div>
          <div className="flex items-center gap-2"><div className="w-3 h-1 rounded bg-red-500" /> Neîncasat</div>
          <span className="ml-auto text-xs">Bordura colorată din stânga fiecărui rând = indicator vizual rapid</span>
        </div>
      )}

      {/* Merge Confirmation Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="w-5 h-5" />
              Combină Evidențe
            </DialogTitle>
            <DialogDescription>
              Selectați care evidență va fi păstrată. Cealaltă va fi ștearsă, iar datele ei vor fi combinate în cea păstrată.
            </DialogDescription>
          </DialogHeader>
          
          {selectedForMerge.length === 2 && (
            <div className="space-y-4">
              <Label className="text-sm font-medium">Alegeți evidența care va fi păstrată:</Label>
              
              {getSelectedMergeRecords().map((record) => {
                const isTarget = targetEvidenceId === record.id;
                const isSource = !isTarget;
                
                return (
                  <Card 
                    key={record.id} 
                    className={`cursor-pointer transition-all ${
                      isTarget 
                        ? "border-green-500 bg-green-50 ring-2 ring-green-500" 
                        : "border-red-200 bg-red-50 hover:border-red-300"
                    }`}
                    onClick={() => setTargetEvidenceId(record.id)}
                  >
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="targetEvidence"
                            checked={isTarget}
                            onChange={() => setTargetEvidenceId(record.id)}
                            className="w-4 h-4 text-green-600"
                          />
                          <div>
                            <p className="font-medium">{record.numar_dosar || record.numar_dosar_intern}</p>
                            <p className="text-sm text-muted-foreground">{record.nume_client}</p>
                            {record.numar_dosar_intern && record.numar_dosar && (
                              <p className="text-xs text-muted-foreground">Nr. intern: {record.numar_dosar_intern}</p>
                            )}
                          </div>
                        </div>
                        <Badge variant={isTarget ? "default" : "destructive"} className={isTarget ? "bg-green-600" : ""}>
                          {isTarget ? "✓ Va fi păstrată" : "Va fi ștearsă"}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Cum funcționează:</strong> Datele financiare din evidența ștearsă vor completa câmpurile goale din evidența păstrată. Valorile existente nu vor fi suprascrise.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>
              Anulează
            </Button>
            <Button onClick={handleMerge} disabled={merging || !targetEvidenceId}>
              {merging ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Se combină...
                </>
              ) : (
                <>
                  <Merge className="w-4 h-4 mr-2" />
                  Confirmă Combinarea
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
